import type { SupabaseClient } from '@supabase/supabase-js';
import { supabaseConfiguration } from '../config/supabase';
import type { HomeHighlightContent } from '../domain/entities/types';
import type { SupabaseDatabase } from '../domain/supabase/database.types';
import { homeHighlightContentRepository } from '../repositories/dexie/repositories';

type HomeHighlightRow = SupabaseDatabase['public']['Tables']['home_highlight']['Row'];

export type HomeHighlightServiceErrorKind = 'forbidden' | 'network' | 'not_configured' | 'unknown';

export class HomeHighlightServiceError extends Error {
  constructor(
    message: string,
    readonly kind: HomeHighlightServiceErrorKind,
    readonly cause?: unknown
  ) {
    super(message);
    this.name = 'HomeHighlightServiceError';
  }
}

export interface HomeHighlightService {
  refresh(): Promise<HomeHighlightContent | undefined>;
  save(content: Omit<HomeHighlightContent, 'id' | 'updatedAt'>): Promise<HomeHighlightContent>;
}

interface ErrorLike { code?: string; message?: string }
const isErrorLike = (error: unknown): error is ErrorLike => typeof error === 'object' && error !== null;

const normalizeError = (error: unknown): HomeHighlightServiceError => {
  if (error instanceof HomeHighlightServiceError) return error;
  const details = isErrorLike(error) ? error : undefined;
  const code = details?.code?.toLowerCase();
  const message = details?.message?.toLowerCase() ?? '';
  if (code === '42501' || message.includes('row-level security') || message.includes('permission denied')) {
    return new HomeHighlightServiceError('Sua conta não tem permissão para editar o destaque.', 'forbidden', error);
  }
  if (error instanceof TypeError || message.includes('fetch') || message.includes('network')) {
    return new HomeHighlightServiceError('Sem conexão para atualizar o destaque global.', 'network', error);
  }
  return new HomeHighlightServiceError('Não foi possível acessar o destaque global.', 'unknown', error);
};

const fromRow = (row: HomeHighlightRow): HomeHighlightContent => ({
  id: 'home',
  published: row.published,
  title: row.title,
  text: row.text,
  reference: row.reference,
  updatedAt: row.updated_at
});

export const createHomeHighlightService = (
  client: SupabaseClient<SupabaseDatabase> | null,
  configurationMessage = client ? 'Supabase configurado.' : 'Configuração do Supabase pendente neste ambiente.',
  cache: Pick<typeof homeHighlightContentRepository, 'put'> = homeHighlightContentRepository
): HomeHighlightService => {
  const requireClient = () => {
    if (!client) throw new HomeHighlightServiceError(configurationMessage, 'not_configured');
    return client;
  };
  const safely = async <Result,>(operation: () => Promise<Result>): Promise<Result> => {
    try { return await operation(); }
    catch (error) { throw normalizeError(error); }
  };

  return {
    async refresh() {
      return safely(async () => {
        const { data, error } = await requireClient()
          .from('home_highlight')
          .select('id, published, title, text, reference, updated_at, updated_by')
          .eq('id', 'home')
          .maybeSingle();
        if (error) throw error;
        if (!data) return undefined;
        const content = fromRow(data);
        await cache.put(content);
        return content;
      });
    },

    async save(value) {
      const content = {
        published: value.published,
        title: value.title.trim(),
        text: value.text.trim(),
        reference: value.reference.trim()
      };
      if (content.title.length > 48 || content.text.length > 280 || content.reference.length > 80) {
        throw new HomeHighlightServiceError('O conteúdo do destaque ultrapassa o limite permitido.', 'unknown');
      }
      return safely(async () => {
        const { data, error } = await requireClient()
          .from('home_highlight')
          .update(content)
          .eq('id', 'home')
          .select('id, published, title, text, reference, updated_at, updated_by')
          .single();
        if (error) throw error;
        const saved = fromRow(data);
        await cache.put(saved);
        return saved;
      });
    }
  };
};

export const homeHighlightService = createHomeHighlightService(
  supabaseConfiguration.client,
  supabaseConfiguration.message
);
